import { Icon, Text } from '@zillow/constellation-10';
import type { PaymentHistoryItemCommon } from '@zg-rentals/applications-utils';
import { IconCheckmarkFilled, IconMinusFilled } from '@zillow/constellation-icons';
import { type PaymentGridCell, PaymentHistoryGridCommon, PaymentNoDataCell } from './PaymentHistoryGridCommon';

type PaymentCodeInfo =
  | {
      type: 'on-time' | 'no-update' | 'no-data' | 'derogatory' | 'bankruptcy';
      description: string;
    }
  | {
      type: 'delinquent';
      delinquentDays?: number;
    };

// Parsed directly from Experian Appendix T
export const PAYMENT_CODE_CONFIG = {
  '': { type: 'no-data', description: 'No history maintained' },
  C: { type: 'on-time', description: 'Current' },
  '0': { type: 'on-time', description: 'Current with zero balance - update received' },
  N: { type: 'on-time', description: 'Current with zero balance - no update received' },
  '1': { type: 'delinquent', delinquentDays: 30 },
  '2': { type: 'delinquent', delinquentDays: 60 },
  '3': { type: 'delinquent', delinquentDays: 90 },
  '4': { type: 'delinquent', delinquentDays: 120 },
  '5': { type: 'delinquent', delinquentDays: 150 },
  '6': { type: 'delinquent', delinquentDays: 180 },
  '7': {
    type: 'bankruptcy',
    description:
      'Bankruptcy chapter 13 petitioned, or discharged and bankruptcy chapter 13 reaffirmation of debt rescinded',
  },
  '8': { type: 'derogatory', description: 'Foreclosure proceedings started, deed in lieu' },
  '9': {
    type: 'derogatory',
    description:
      'Settled, insurance claim, term default, government claim, paid by dealer, BK chapter 7, 11 or 12 petitioned, or discharged and BK chapter 7, 11 or 12 reaffirmation of debt rescinded',
  },
  G: { type: 'derogatory', description: 'Collection' },
  H: { type: 'derogatory', description: 'Foreclosure' },
  J: { type: 'derogatory', description: 'Voluntary surrender' },
  K: { type: 'derogatory', description: 'Repossession' },
  L: { type: 'derogatory', description: 'Charge off' },
  B: { type: 'no-update', description: 'Account condition changed - payment code not applicable' },
  '-': { type: 'no-update', description: 'No update received' },
} satisfies Record<string, PaymentCodeInfo>;
type PaymentCode = keyof typeof PAYMENT_CODE_CONFIG;

export function PaymentCellIcon({ info }: { info: PaymentCodeInfo }) {
  switch (info.type) {
    case 'on-time':
      return (
        <Icon role="img" title={info.description} css={{ color: 'green.600' }}>
          <IconCheckmarkFilled />
        </Icon>
      );
    case 'no-update':
      return (
        <Icon role="img" title={info.description} css={{ color: 'gray.300' }}>
          <IconMinusFilled />
        </Icon>
      );
    case 'derogatory':
      // This should never be seen on the UI, derogatory codes don't belong to the payment history grid
      return (
        <Text
          textStyle="body-sm-bold"
          css={{
            color: 'red.600',
          }}
        >
          {
            /* Fallback to the first letter of description as an icon */
            info.description.substring(0, 1)
          }
        </Text>
      );
    case 'delinquent':
      return (
        <Text
          textStyle="body-sm-bold"
          css={{
            color: 'yellow.500',
          }}
        >
          {info.delinquentDays}
        </Text>
      );

    // Bankruptcy DOES NOT appear in the payment history grid by design
    case 'no-data':
    case 'bankruptcy':
      return <PaymentNoDataCell />;
  }
  return info satisfies never;
}

function getPaymentCodeLegend(code: PaymentCodeInfo) {
  switch (code.type) {
    case 'delinquent':
      return `${code.delinquentDays} days past due`;
    case 'derogatory':
      return code.description;
    case 'no-data':
    case 'bankruptcy':
      // Bankruptcy DOES NOT appear in payment history grid by design
      return 'No history maintained';
    case 'no-update':
      return 'No history reported';
    case 'on-time':
      return 'Paid on time';
    default:
      return code satisfies never;
  }
}

export function PaymentHistoryGridLegacy({ items }: { items: Array<PaymentHistoryItemCommon> }) {
  const cells: Array<PaymentGridCell> = items.map(({ year, month, code }) => {
    const info = PAYMENT_CODE_CONFIG[code as PaymentCode] ?? PAYMENT_CODE_CONFIG[''];
    return { year, month, code, cell: <PaymentCellIcon info={info} />, legend: getPaymentCodeLegend(info) };
  });

  return <PaymentHistoryGridCommon cells={cells} />;
}
